import { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, TrendingDown, FileText } from "lucide-react";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Bar, ComposedChart, Line, LineChart, Legend } from "recharts";

const MONTHS = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
const LINE_COLORS = ["#94a3b8", "#f59e0b", "#0ea5e9", "#3B82F6"];
const compact = (v) => `₹${Intl.NumberFormat("en-IN", { notation: "compact", maximumFractionDigits: 1 }).format(v)}`;

export const FyComparisonDialog = ({ open, onOpenChange }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (open) api.get("/dashboard/fy-comparison").then((r) => setData(r.data)).catch(() => setData([]));
  }, [open]);

  const monthly = data
    ? MONTHS.map((m, i) => ({ month: m, ...Object.fromEntries(data.map((f) => [f.label, f.monthly[i]])) }))
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-y-auto content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right" data-testid="fy-comparison-dialog">
        <DialogHeader><DialogTitle>Financial Year Comparison — Turnover &amp; Profit</DialogTitle></DialogHeader>
        {data == null ? (
          <div className="space-y-3"><Skeleton className="h-24 w-full" /><Skeleton className="h-56 w-full" /></div>
        ) : data.length === 0 ? (
          <p className="text-sm text-slate-500 py-6" data-testid="fy-empty">No invoices yet — turnover comparison will appear once invoices are generated.</p>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {data.map((f) => (
                <div key={f.fy_start} className="border border-slate-200 rounded-xl p-4 bg-slate-50/60" data-testid={`fy-card-${f.fy_start}`}>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{f.label}</div>
                  <div className="font-display text-xl font-semibold text-slate-900 tabular-nums mt-1.5">{inr(f.turnover)}</div>
                  <div className="text-[11px] text-slate-400 tabular-nums" data-testid={`fy-ex-gst-${f.fy_start}`}>{inr(f.turnover_ex_gst)} ex-GST</div>
                  <div className="text-[11px] text-slate-500 mt-1.5 space-y-0.5">
                    <div>Profit: <span className="font-semibold text-emerald-700 tabular-nums">{inr(f.profit)}</span></div>
                    <div className="flex items-center gap-1" data-testid={`fy-invoice-count-${f.fy_start}`}><FileText size={10} /> {f.count} invoice{f.count === 1 ? "" : "s"}{f.returned_count > 0 ? ` (${f.returned_count} returned)` : ""}</div>
                  </div>
                  {f.growth_pct != null && (
                    <div className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 mt-2 ${f.growth_pct >= 0 ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"}`}>
                      {f.growth_pct >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />} {f.growth_pct >= 0 ? "+" : ""}{f.growth_pct}% YoY
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Turnover vs Profit by FY</div>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={compact} width={70} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="turnover" name="Turnover" fill="#3B82F6" radius={[6, 6, 0, 0]} maxBarSize={56} />
                  <Bar dataKey="profit" name="Profit" fill="#10b981" radius={[6, 6, 0, 0]} maxBarSize={56} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Month-wise Trend (Apr → Mar)</div>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={compact} width={70} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {data.map((f, i) => (
                    <Line key={f.fy_start} type="monotone" dataKey={f.label} dot={{ r: 2.5 }}
                          stroke={LINE_COLORS[(LINE_COLORS.length - data.length + i) % LINE_COLORS.length]}
                          strokeWidth={f.fy_start === data[data.length - 1].fy_start ? 2.5 : 1.5} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
